import type { ReactElement } from 'react';
import { useEffect } from 'react';

import { ApplicationScreen } from '@/features/application/components/ApplicationScreen';
import { HaveYouAppliedScreen } from '@/features/applied/components/HaveYouAppliedScreen';
import { DrugTestScreen } from '@/features/drugtest/components/DrugTestScreen';
import { EnrolmentAnnouncement } from '@/features/enrolment/components/EnrolmentAnnouncement';
import { EnrolmentFlow } from '@/features/enrolment/components/EnrolmentFlow';
import { ENROLMENT_COPY_LANGUAGE } from '@/features/enrolment/copy';
import { useLiveStatusSocket } from '@/features/enrolment/hooks/useLiveStatusSocket';
import { useVenueCheckOnReset } from '@/features/enrolment/hooks/useVenueCheckOnReset';
import { FitQuestionsScreen } from '@/features/fit/components/FitQuestionsScreen';
import { I9Screen } from '@/features/i9/components/I9Screen';
import { ProfileUpdateScreen } from '@/features/profileupdate/components/ProfileUpdateScreen';
import { SubmissionFailedScreen } from '@/features/submitted/components/SubmissionFailedScreen';
import { SubmittedScreen } from '@/features/submitted/components/SubmittedScreen';
import type { Enrolment } from '@/lib/enrolmentSession';
import { ENROLMENT, useEnrolmentSession } from '@/lib/enrolmentSession';
import type { ThrottledResult } from '@/services/identityLookup';

import { ArrivalReasonScreen } from './features/arrival/components/ArrivalReasonScreen';
import { AssociateStatusScreen } from './features/associate/components/AssociateStatusScreen';
import { CheckInScreen } from './features/checkin/components/CheckInScreen';
import { AllDoneScreen } from './features/finish/components/AllDoneScreen';
import { FinishScreen } from './features/finish/components/FinishScreen';
import { JobDetailScreen } from './features/jobs/components/JobDetailScreen';
import { JobsScreen } from './features/jobs/components/JobsScreen';
import { NoJobsScreen } from './features/jobs/components/NoJobsScreen';
import { PayCardAskScreen } from './features/paycard/components/PayCardAskScreen';
import { PayCardLinkedScreen } from './features/paycard/components/PayCardLinkedScreen';
import { PayCardNeedScreen } from './features/paycard/components/PayCardNeedScreen';
import { PayCardScreen } from './features/paycard/components/PayCardScreen';
import { PayCardWhyScreen } from './features/paycard/components/PayCardWhyScreen';
import { CheckingScreen } from './features/routing/components/CheckingScreen';
import { RoutingScreen } from './features/routing/components/RoutingScreen';
import { ThrottledScreen } from './features/throttle/components/ThrottledScreen';
import { WelcomeScreen } from './features/welcome/components/WelcomeScreen';
import type { LookupStatus, Screen } from './lib/kioskSession';
import { LOOKUP_STATUS, SCREEN, useKioskSession } from './lib/kioskSession';
import { CHECK_IN_IDLE_TIMEOUT_MS, useIdleTimeout } from './shared/hooks/useIdleTimeout';

/**
 * The kiosk flow: device enrolment (KAD-6c), then the Welcome doors (KID-1), the check-in form they open,
 * and the three places a lookup can land (KID-2).
 *
 * ⭐ **Enrolment comes FIRST, and it is a gate rather than a screen in the flow.** Every load asks the
 * server whether this device is still enrolled and renders the answer [03-us-kad-6i], so a device the
 * system has never seen reaches the PIN pad [KAD-6c AC-1] and one it still honours reaches the candidate
 * journey — with nobody typing six digits for an enrolment that was never lost. A device whose credential
 * the server refuses is — from the server's view — exactly that same unenrolled device, so a `401` on any
 * candidate-journey call brings it back here [decision 0245]. The candidate's session is cleared before that
 * navigation happens, in `enrolmentSession.ts`, so the PIN pad is always a cleared state: that is what keeps
 * the idle timer's `screen !== welcome` condition — written before a PIN pad existed — from leaving a
 * stranger's last-4 SSN live on an unattended machine.
 *
 * Not code-split with `React.lazy` on purpose. Convention skill § 5 asks for route-level splitting because
 * this app boots on cheap hardware over branch-office Wi-Fi — but the whole flow is a few kilobytes, and a
 * separate chunk fetched at the moment a candidate taps a door would trade a smaller first paint for a
 * network round trip mid-application on exactly that Wi-Fi. Revisit when a screen carries real weight.
 */
export function App() {
  const enrolment = useEnrolmentSession((state) => state.enrolment);
  const checkEnrolment = useEnrolmentSession((state) => state.checkEnrolment);
  const recheckEnrolment = useEnrolmentSession((state) => state.recheckEnrolment);
  const screen = useKioskSession((state) => state.screen);
  const language = useKioskSession((state) => state.language);
  const venueDefaultLanguage = useKioskSession((state) => state.venueDefaultLanguage);
  const lookupStatus = useKioskSession((state) => state.lookupStatus);
  const endCandidateSession = useKioskSession((state) => state.endCandidateSession);
  const throttle = useKioskSession((state) => state.throttle);

  /**
   * The clear-on-idle rule [KID-1], armed ONCE for the whole flow rather than per screen.
   *
   * It was per screen, and that is precisely how it went wrong twice over. Two screens imported the hook
   * and never called it — a failure with no symptom, since everything works and only the next person to
   * walk up sees the previous candidate's half-filled form. And nine more never imported it at all:
   * Jobs, Job detail, No jobs, the two fit screens, Submitted, I-9, Pay card and Finish.
   *
   * That second gap is the one that matters, because `jobs` is where the flow goes the instant the profile
   * is saved. A candidate who walks away on the openings list left their last-4 SSN and their birthday live
   * in the store on an unattended lobby machine, with no timer anywhere to clear them.
   *
   * `welcome` is excluded because it IS the cleared state — arming there would restart a timer on an empty
   * session forever, and `endCandidateSession` is what the timer calls.
   *
   * ⭐ **With ONE exception, and it is a real leak** [03-us-kad-1e AC-16; Operator ruling, 2026-09-22:
   * *"all resets default the language back to the configuration set from the admin"*]. A candidate can
   * switch to English on Welcome, open no door at all, and walk away — nothing else on that screen changes
   * the session, so there is nothing for the rule above to clear, and the NEXT candidate at a Spanish venue
   * meets an English kiosk. That is the failure AC-16 names, reachable with no error condition anywhere.
   * ⛔ **It is not a loop**: the timer is armed only while the two languages differ, and firing makes them
   * equal, which disarms it. A cleared Welcome at a venue whose language is on screen arms nothing.
   *
   * The throttle countdown gets a longer one, for the reason in {@link idleTimeoutMsFor}.
   */
  useIdleTimeout(
    endCandidateSession,
    screen !== SCREEN.welcome || language !== venueDefaultLanguage,
    idleTimeoutMsFor(screen, throttle),
  );

  /*
   * WCAG 3.1.1: the document has to declare the language it is actually RENDERING, or a screen reader
   * announces English chrome in a Spanish voice.
   *
   * ⭐ **Which is why it follows the candidate journey and not the store** [03-us-kad-1e AC-14]. The nine
   * device-enrolment states are English literals by [decision 0226] — the PIN pad, *Connecting*, the naming
   * step and *Ready* — and since this story a tablet at a Spanish venue
   * carries `language: 'es'` from the moment its enrolment lands, which is BEFORE any of them render.
   * Writing `es` over an English screen is the exact failure the attribute exists to prevent, so the
   * enrolment branch declares the language its own copy is written in.
   *
   * ⛔ **This is not the [0226] / AC-6 seam** and it translates nothing: it declares what is on the screen
   * rather than changing it. Whether those screens SHOULD speak the venue's language is a Ground Truth
   * question and is owed elsewhere.
   */
  useEffect(() => {
    document.documentElement.lang =
      enrolment === ENROLMENT.enrolled ? language : ENROLMENT_COPY_LANGUAGE;
  }, [enrolment, language]);

  /**
   * ⭐ **Every load asks the server whether this device is still enrolled, and renders the answer**
   * [03-us-kad-6i AC-7; decision 0280] — a first load, a browser reload, a restored tab or a power-cycle.
   *
   * ⛔ **No enrolment screen commits before that answer or its failure is known.** The store opens on the
   * interim state rather than on the pad, so a reloaded tablet shows a wait and then the candidate journey,
   * where it used to show a PIN pad and then flip — a pad flash in front of a staffer on every reload, and
   * six digits typed for an enrolment that was never lost.
   *
   * ⛔ **Exactly one request, ever.** The store holds the single-flight lock, so `StrictMode`'s double
   * effect in development and any re-render here issue one probe between them.
   */
  useEffect(() => {
    void checkEnrolment('boot');
  }, [checkEnrolment]);

  /**
   * ⭐ **The ONE bounded, user-visible recovery out of a boot that got no answer** [AC-10].
   *
   * ⚠️ **Without it the no-outcome state is terminal, and the blast radius is the estate**: a thirty-second
   * store blip strands every tablet until a human walks up to each one, and the practical remedy is
   * re-typing the PIN — the exact cost this story exists to remove.
   *
   * ⛔ **Never a loop and never a timer** [`0262`'s *silently retry*]. Each event produces at most one
   * probe, and the store bounds this door — a floor, an idle window on a pad somebody is typing at, and a
   * jitter — ⭐ **because a signal is not a person**: an AP flap delivers `online` to every tablet behind a
   * branch address in the same instant.
   *
   * ⚠️ **It is the SECOND recovery, not the only one.** Neither event fires during a server-side outage —
   * the device's link stays up — which is the outage the paragraph above describes. ⭐ **The pad's *Try
   * again* carries that case** [`PinScreen`], unbounded and immediate, because a person asked.
   */
  useEffect(() => {
    const recover = () => {
      // `visibilitychange` fires on the way OUT as well, and a hidden tablet has nobody to recover for —
      // without this, one hide→show cycle is two probes and the second runs while the screen is dark.
      if (document.visibilityState !== 'visible') return;
      void recheckEnrolment('signal');
    };

    window.addEventListener('online', recover);
    document.addEventListener('visibilitychange', recover);
    return () => {
      window.removeEventListener('online', recover);
      document.removeEventListener('visibilitychange', recover);
    };
  }, [recheckEnrolment]);

  /**
   * ⭐ **The live status socket** [03-us-kad-6j-b] — held while the tablet is enrolled and in front, closed
   * on a hide or a blur, reopened on the way back. ⛔ It never probes: the listener above stays the only
   * thing a show can make ask `GET /enrolment/me`.
   */
  useLiveStatusSocket();

  /**
   * ⭐ **A visit ending asks once whether the venue still serves** [03-us-kad-6o AC-1; 0433] — the reset
   * half of *at boot and at every return to the welcome, and never during a visit*. The welcome's first tap
   * is the other half, on `WelcomeScreen` [AC-2; 0430 § 1].
   */
  useVenueCheckOnReset();

  return (
    <>
      {/*
        ⭐ **ABOVE the enrolment fork, and that is the criterion rather than a layout choice** [eval row
        **O1**; decision `0307`]. `screenFor` returns `<EnrolmentFlow>` only while the device is unenrolled,
        so a live region inside that subtree is unmounted by the verdict — and on a healthy network the boot
        probe is sub-second, so a `polite` announcement queued behind another utterance never drains. Here it
        survives every verdict, and its text is cleared when one lands.
      */}
      <EnrolmentAnnouncement />
      {screenFor(enrolment, lookupStatus, screen)}
    </>
  );
}

/**
 * Which screen the app is showing, in the order the two gates apply.
 *
 * ⛔ **Nothing candidate-facing renders on a device that is not enrolled** — not the welcome, not a job
 * list, not an empty one, and ⛔ **not while a boot probe is still in flight** [03-us-kad-6i AC-15]. It is a
 * function rather than three early returns in the component because the live region above it has to render
 * on every one of these branches.
 *
 * The lookup's loading state replaces the whole screen rather than dimming the form [Figma · 03 · Routing].
 * The frame draws a card with a spinner and nothing else — no fields behind it — so a candidate cannot keep
 * typing into inputs whose values are already in flight.
 */
function screenFor(enrolment: Enrolment, lookupStatus: LookupStatus, screen: Screen): ReactElement {
  if (enrolment !== ENROLMENT.enrolled) return <EnrolmentFlow enrolment={enrolment} />;
  if (lookupStatus === LOOKUP_STATUS.pending) return <CheckingScreen />;
  return SCREENS[screen];
}

/**
 * How long this screen may sit untouched — two minutes everywhere except the throttle countdown.
 *
 * ⚠️ **The countdown is designed to be watched, not touched, and a flat timer destroys it.** Its only
 * control is disabled for the whole wait [ThrottledScreen], the ticking numbers dispatch no pointer or key
 * events, and the shortest backoff is three minutes [0064] — so a two-minute idle timer always fires first.
 * The candidate is dumped to Welcome with their factors wiped, one minute before the wait they were serving
 * would have let them back in. That is the self-recovery 0064 exists to provide, deleted; at the
 * sixty-minute ceiling it becomes a loop, bouncing them every two minutes forever.
 *
 * So the countdown is given its own wait plus the usual two minutes on top — long enough to serve it and
 * then notice the button has come alive. It still ends: the session is cleared rather than left standing,
 * which matters most at the ceiling, where the alternative is a last-4 SSN sitting on a lobby screen for an
 * hour.
 */
function idleTimeoutMsFor(screen: Screen, throttle: ThrottledResult | undefined): number {
  if (screen !== SCREEN.throttled || throttle === undefined) return CHECK_IN_IDLE_TIMEOUT_MS;
  return throttle.retryAfterSeconds * 1000 + CHECK_IN_IDLE_TIMEOUT_MS;
}

/**
 * Every screen the flow can be in, exhaustively.
 *
 * A `Record<Screen, …>` rather than a chain of ternaries: adding a screen to the state machine without
 * giving it somewhere to render becomes a typecheck failure here, instead of a blank display in a lobby.
 */
const SCREENS: Record<Screen, ReactElement> = {
  [SCREEN.welcome]: <WelcomeScreen />,
  [SCREEN.haveYouApplied]: <HaveYouAppliedScreen />,
  [SCREEN.checkIn]: <CheckInScreen />,
  [SCREEN.routing]: <RoutingScreen />,
  [SCREEN.throttled]: <ThrottledScreen />,
  [SCREEN.application]: <ApplicationScreen />,
  [SCREEN.fitQuestions]: <FitQuestionsScreen />,
  [SCREEN.submitted]: <SubmittedScreen />,
  [SCREEN.submissionFailed]: <SubmissionFailedScreen />,
  [SCREEN.profileUpdate]: <ProfileUpdateScreen />,
  [SCREEN.arrivalReason]: <ArrivalReasonScreen />,
  [SCREEN.jobs]: <JobsScreen />,
  [SCREEN.jobDetail]: <JobDetailScreen />,
  [SCREEN.noJobs]: <NoJobsScreen />,
  [SCREEN.associateStatus]: <AssociateStatusScreen />,
  [SCREEN.i9]: <I9Screen />,
  [SCREEN.payCardWhy]: <PayCardWhyScreen />,
  [SCREEN.payCardAsk]: <PayCardAskScreen />,
  [SCREEN.payCardNeed]: <PayCardNeedScreen />,
  [SCREEN.payCard]: <PayCardScreen />,
  [SCREEN.payCardLinked]: <PayCardLinkedScreen />,
  [SCREEN.drugTest]: <DrugTestScreen />,
  [SCREEN.finish]: <FinishScreen />,
  [SCREEN.allDone]: <AllDoneScreen />,
};
